import 'vanilla-cookieconsent';
import en from './Language/en.json';
import de from './Language/de.json';
import fr from './Language/fr.json';
import it from './Language/it.json';
import pt from './Language/pt.json';
import sq from './Language/sq.json';

// Intercept XHR and Fetch requests to block tracking calls
(function () {
    // Helper function to determine whether a request should be blocked
    function shouldBlockRequest(url) {
        const blockedDomains = [
            'google-analytics.com',
            'analytics.matomo.com',
            'facebook.com',
            'doubleclick.net',
            'matomo-stopsmoking.ch'
        ];

        // Only block requests if the user has not consented to analytics
        return blockedDomains.some(domain => url.includes(domain)) && !window.cc?.allowedCategory('analytics');
    }

    // Intercept XHR requests
    const originalXHR = window.XMLHttpRequest;

    class CustomXHR extends originalXHR {
        constructor() {
            super();
            this.addEventListener('readystatechange', function () {
                if (this.readyState === 1) {
                    const url = this.responseURL || this._url;
                    if (shouldBlockRequest(url)) {
                        console.warn('Blocked XHR call to:', url);
                        this.abort(); // Cancel the request
                    } else {
                        //console.log('XHR call to:', url);
                    }
                }
            });
        }

        open(method, url, async, user, password) {
            this._url = url; // Store the URL for later use
            super.open(method, url, async, user, password);
        }
    }

    window.XMLHttpRequest = CustomXHR; // Override the default XHR

    // Intercept Fetch requests
    const originalFetch = window.fetch;

    window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : input.url;
        if (shouldBlockRequest(url)) {
            console.warn('Blocked Fetch call to:', url);
            return Promise.reject(new Error('Blocked by cookie consent'));
        }
        return originalFetch(input, init);
    };
})();

// CookieConsent implementation
export default class CookieConsent {
    constructor() {
        this.init().then(() => {
            window.cc = initCookieConsent();
            cc.run(this.options);
        });
    }

    async init() {
        this.languages = {
            'en': en,
            'de': de,
            'fr': fr,
            'it': it,
            'pt': pt,
            'sq': sq
        };

        if (window.frpCookieConsentSettings && window.frpCookieConsentSettings.overrideJsonPaths) {
            const fetchPromises = [];
            for (const [key, value] of Object.entries(window.frpCookieConsentSettings.overrideJsonPaths)) {
                const promise = fetch(value)
                    .then(response => response.json())
                    .then(data => {
                        this.languages[key] = data;
                    });
                fetchPromises.push(promise);
            }
            await Promise.all(fetchPromises);
        }

        this.setupOptions();
    }

    setupOptions() {
        const that = this;

        const html = document.querySelector('html');
        const lang = html.getAttribute('lang');
        const langCode = lang.split('-')[0];

        this.options = {
            current_lang: langCode,
            autoclear_cookies: true,
            cookie_name: 'cc_cookie',
            cookie_expiration: 365,
            page_scripts: true,
            force_consent: true,

            gui_options: {
                consent_modal: {
                    layout: 'cloud',
                    position: 'bottom center',
                    transition: 'slide'
                },
                settings_modal: {
                    layout: 'box',
                    position: 'left',
                    transition: 'slide'
                }
            },

            onFirstAction: function () {
                if (cc.allowedCategory('analytics')) {
                    // Optional: Reload the page or handle other actions
                }
            },

            onAccept: function (cookie) {
                // console.log('onAccept fired!');
                that.changeConsent();
            },

            onChange: function (cookie, changed_preferences) {
                // console.log('onChange fired!');
                that.changeConsent();
            },

            languages: this.languages
        };

        if (typeof frpCookieConsentSettings !== 'undefined' && frpCookieConsentSettings.hasOwnProperty('vanillaCookieOptions')) {
            this.options = Object.assign(this.options, frpCookieConsentSettings.vanillaCookieOptions);
        }
    }

    changeConsent() {
        const analyticsAllowed = cc.allowedCategory('analytics');
        const targetingAllowed = cc.allowedCategory('targeting');

        // console.log('Analytics allowed:', analyticsAllowed);
        // console.log('Targeting allowed:', targetingAllowed);

        // Matomo Consent Handling (Single Push)
        if (typeof _paq !== 'undefined') {
            if (!analyticsAllowed) {
                _paq.push(['forgetConsentGiven', 'optUserOut']); // Combine both into a single push
                // console.log('Matomo: Consent revoked.');
            } else {
                _paq.push(['setConsentGiven']); // Only push one event when consent is granted
                // console.log('Matomo: Consent granted.');
            }
        }

        // Consolidated GTAG Consent Update
        if (typeof gtag === 'function') {
            gtag('consent', 'update', {
                'analytics_storage': analyticsAllowed ? 'granted' : 'denied',
                'ad_storage': targetingAllowed ? 'granted' : 'denied',
                'ad_user_data': targetingAllowed ? 'granted' : 'denied',
                'ad_personalization': targetingAllowed ? 'granted' : 'denied',
            });

            // console.log(`GTAG CONSENT UPDATE - Analytics: ${analyticsAllowed ? 'granted' : 'denied'}, Targeting: ${targetingAllowed ? 'granted' : 'denied'}`);
        }

        // Consolidated Facebook Pixel Consent
        if (typeof fbq === 'function') {
            fbq('consent', targetingAllowed ? 'grant' : 'revoke');
            // console.log(`FBQ CONSENT UPDATE - ${targetingAllowed ? 'granted' : 'revoked'}`);
        }

        // Fire a single GTM and Matomo Tag Manager event for consent change
        if (typeof dataLayer !== 'undefined') {
            dataLayer.push({'event': 'consent_change'});
        }
        if (typeof _mtm !== 'undefined') {
            _mtm.push({'event': 'consent_change'});
        }

        // console.log('Consent change event pushed to GTM and Matomo.');
    }
}
